/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */
import {
  nodesApi,
  searchApi,
  sharedLinksApi,
  versionsApi,
} from "../AlfrescoApi.js";
import errorService, { newPristyError } from "./ErrorService.js";
import QuickLRU from "quick-lru";

/**
 * Service providing operations for managing Alfresco nodes (documents, folders, etc.).
 */
class AlfrescoNodeService {
  constructor() {
    this.lruNode = new QuickLRU({
      maxSize: 50,
      maxAge: 30000, // 30 Sec
    });
    this.lruChildren = new QuickLRU({
      maxSize: 50,
      maxAge: 50000, // 5 Sec
    });
    this.lruParents = new QuickLRU({
      maxSize: 20,
      maxAge: 30000, // 30 Sec
    });
    this.lruDocumentLibrary = new QuickLRU({
      maxSize: 20,
      maxAge: 2000, // 2 Sec
    });
    this.lruVersionHistory = new QuickLRU({
      maxSize: 5,
      maxAge: 10000, // 10 Sec
    });
    this.lruHomeFolder = new QuickLRU({
      maxSize: 50,
      maxAge: 5 * 60 * 1000, // 5m
    });
    this.lruIsFolderInUserHome = new QuickLRU({
      maxSize: 50,
      maxAge: 5 * 60 * 1000, // 5m
    });
  }
  /**
   * Retrieves a node from the nodes API with the specified attributes.
   *
   * @param {string} id The unique identifier of the node to retrieve.
   * @return {Promise<Node>} A promise that resolves to the retrieved node object containing its details.
   */
  getNode(id) {
    if (this.lruNode.has(id)) {
      return Promise.resolve(this.lruNode.get(id));
    }

    const opts = {
      include: [
        "isFavorite",
        "properties",
        "allowableOperations",
        "permissions",
        "path",
        "isLocked",
      ],
    };

    return nodesApi.getNode(id, opts).then((data) => {
      this.lruNode.set(id, data.entry);
      return data.entry;
    });
  }

  /**
   * Retrieves the content of a specific node.
   *
   * @param {string} nodeId - The unique identifier of the node for which content is being retrieved.
   * @param {boolean} attachment - A flag indicating whether to retrieve the content as an attachment.
   * @return {Promise<Blob>} A promise that resolves to the content of the node, or rejects with an error if the operation fails.
   */
  getNodeContent(nodeId, attachment) {
    const opts = {
      attachment: attachment,
    };
    return nodesApi.getNodeContent(nodeId, opts).catch((error) => {
      throw newPristyError("getVersionContent", error);
    });
  }

  /**
   * Creates a new node using the specified identifier and options.
   *
   * @param {string} id - The unique identifier for the node to be created.
   * @param {NodeBodyCreate} opts - An object containing configuration options for the node.
   * @return {Promise<NodeEntry>} A promise that resolves to the created node object.
   * @throws {Error} If the node creation fails, an error will be thrown with additional details.
   */
  createNode(id, opts) {
    return nodesApi.createNode(id, opts).catch((error) => {
      throw newPristyError("createNodeError", error);
    });
  }

  /**
   * Fetch children of a node. If node is a "space", fetch only children from documentLibrary.
   * Check pagination.hasMoreItems to check if you need to load more elements.
   * @param id parent node id
   * @param maxItems The maximum number of items to return in the list..
   * @param skipCount Number of items already loaded.
   * @param refresh boolean, if true, refresh the cache
   * @return {Promise<{documentLibrary: *, children: *, pagination: *} | {children: *, pagination: *}>}
   */
  getChildren(id, maxItems = 10, skipCount = 0, refresh = false) {
    const opts = {
      include: [
        "isFavorite",
        "properties",
        "allowableOperations",
        "permissions",
        "path",
        "isLocked",
      ],
      maxItems: maxItems,
      skipCount: skipCount,
    };

    return this.getChildrenWithOpts(id, opts, refresh);
  }

  /**
   * Fetch children of a node. If node is a "space", fetch only children from documentLibrary.
   * Please take care of pagination.
   * @param id parent node id
   * @param opts options send to nodesApi.listNodeChildren
   * @param refresh boolean, if true, refresh the cache
   * @return {Promise<{documentLibrary: *, children: *, pagination: *} | {children: *, pagination: *}>}
   */
  getChildrenWithOpts(id, opts, refresh = false) {
    const cacheKey = JSON.stringify({ id, opts });
    if (!refresh && this.lruChildren.has(cacheKey)) {
      return Promise.resolve(this.lruChildren.get(cacheKey));
    }

    const def = {
      include: [
        "isFavorite",
        "properties",
        "allowableOperations",
        "permissions",
        "path",
        "isLocked",
      ],
    };
    const mergedOpts = { ...def, ...opts };

    // First call WITHOUT pagination to detect documentLibrary
    // This is needed because sites have documentLibrary as their only direct child
    const detectOpts = { ...mergedOpts };
    delete detectOpts.skipCount;
    delete detectOpts.maxItems;

    return nodesApi.listNodeChildren(id, detectOpts).then((data) => {
      let children = data.list.entries.map((entry) => entry.entry);
      let documentLibrary = children.find(
        (child) => child.name === "documentLibrary",
      );

      if (documentLibrary) {
        // This is a site - get children from documentLibrary with pagination
        return nodesApi
          .listNodeChildren(documentLibrary.id, mergedOpts)
          .then((subData) => {
            const result = {
              documentLibrary: documentLibrary,
              children: subData.list.entries.map((entry) => entry.entry),
              pagination: subData.list.pagination,
            };
            this.lruChildren.set(cacheKey, result);
            return result;
          });
      } else {
        // Not a site - get children with pagination
        return nodesApi
          .listNodeChildren(id, mergedOpts)
          .then((paginatedData) => {
            const result = {
              children: paginatedData.list.entries.map((entry) => entry.entry),
              pagination: paginatedData.list.pagination,
            };
            this.lruChildren.set(cacheKey, result);
            return result;
          });
      }
    });
  }

  /**
   * Fetch children of a node. If node is a "space", return all containers like documentLibrary.
   * Please take care of pagination.
   * @param id parent node id
   * @param opts options send to nodesApi.listNodeChildren
   * @return {Promise<{documentLibrary: *, children: *, pagination: *} | {children: *, pagination: *}>}
   */
  getDirectChildren(id, opts) {
    const cacheKey = JSON.stringify({ id, opts, direct: true });
    if (this.lruChildren.has(cacheKey)) {
      return Promise.resolve(this.lruChildren.get(cacheKey));
    }

    const def = {
      include: [
        "isFavorite",
        "properties",
        "allowableOperations",
        "permissions",
        "path",
        "isLocked",
      ],
    };
    opts = { ...def, ...opts };

    return nodesApi.listNodeChildren(id, opts).then((data) => {
      let children = data.list.entries.map((data) => data.entry);
      const result = {
        children: children,
        pagination: data.list.pagination,
      };
      this.lruChildren.set(cacheKey, result);
      return result;
    });
  }

  /**
   * Updates a node with the specified identifier and options.
   *
   * @param {string} id - The identifier of the node to be updated.
   * @param {Object} nodeBodyUpdate - An object containing the update options for the node.
   * @return {Promise} A promise that resolves when the node is successfully updated or rejects with an error if the update fails.
   */
  updateNode(id, nodeBodyUpdate) {
    return nodesApi
      .updateNode(id, nodeBodyUpdate)
      .then((data) => {
        this.lruNode.delete(id);
        return data;
      })
      .catch((error) => {
        throw errorService.updateNodeError(error);
      });
  }

  /**
   * Update the MIME type of a node's content without changing the content itself.
   * Downloads the current content and re-uploads it with the new MIME type.
   * @param {string} id - The node identifier.
   * @param {string} mimeType - The new MIME type (e.g. "text/plain").
   * @return {Promise} A promise that resolves with the updated node entry.
   */
  updateMimeType(id, mimeType) {
    return nodesApi
      .getNodeContent(id)
      .then((content) => {
        return nodesApi.put({
          path: "/nodes/{nodeId}/content",
          pathParams: { nodeId: id },
          queryParams: { majorVersion: false },
          contentTypes: [mimeType],
          bodyParam: content,
        });
      })
      .catch((error) => {
        throw errorService.updateNodeError(error);
      });
  }

  /**
   * Create a version if not existe.
   * @param id
   */
  checkVersion(id) {
    return nodesApi
      .getNode(id)
      .then((data) => {
        return data.entry;
      })
      .then((node) => {
        if (!node.aspectNames.find((aspect) => aspect === "cm:versionable")) {
          // Add aspect
          const opts = { aspectNames: [...node.aspectNames, "cm:versionable"] };
          return nodesApi
            .updateNode(id, opts)
            .then(() => {
              // Iniale Version
              return "1.0";
            })
            .catch((error) => {
              if (error.status === 403) {
                console.warn("Can’t add version - Forbidden");
              } else {
                throw newPristyError("updateNode", error);
              }
            });
        }
      });
  }

  deleteNode(id, type) {
    const opts = {};

    return nodesApi.deleteNode(id, opts).catch((error) => {
      errorService.deleteNodeError(error, type);
    });
  }

  getParents(id) {
    if (this.lruParents.has(id)) {
      return Promise.resolve(this.lruParents.get(id));
    }

    const opts = {
      include: ["isFavorite", "properties", "path"],
    };

    return nodesApi.listParents(id, opts).then((data) => {
      let parents = data.list.entries.map((data) => data.entry);
      let documentLibrary = parents[0];
      if (documentLibrary && documentLibrary.name === "documentLibrary") {
        return this.getParents(documentLibrary.id).then((supParent) => {
          const result = {
            documentLibrary: documentLibrary,
            supParent: supParent,
          };
          this.lruParents.set(id, result);
          return result;
        });
      } else {
        this.lruParents.set(id, parents[0]);
        return parents[0];
      }
    });
  }

  getDocumentLibrary(id) {
    if (this.lruDocumentLibrary.has(id)) {
      return Promise.resolve(this.lruDocumentLibrary.get(id));
    }

    const opts = {
      include: [
        "isFavorite",
        "properties",
        "allowableOperations",
        "permissions",
      ],
    };

    return nodesApi.listNodeChildren(id, opts).then((data) => {
      let children = data.list.entries.map((data) => data.entry);
      const docLib = children.find((child) => child.name === "documentLibrary");
      this.lruDocumentLibrary.set(id, docLib);
      return docLib;
    });
  }

  updateContentNode(id, bin, version, comment) {
    let opts = {
      majorVersion: version,
      comment: comment,
    };
    return nodesApi.updateNodeContent(id, bin, opts).catch((error) => {
      throw newPristyError("updateContentNode", error);
    });
  }

  copyNode(id, destination, name) {
    const nodeBody =
      name !== null
        ? {
            targetParentId: destination,
            name: name,
          }
        : {
            targetParentId: destination,
          };

    const opts = {
      include: ["path"],
    };

    return nodesApi.copyNode(id, nodeBody, opts).catch((error) => {
      throw newPristyError("copyNode", error);
    });
  }

  moveNode(id, targetParentId, newName, opts) {
    if (!id) {
      throw new Error(`moveNode: mandatory id`);
    }
    if (!targetParentId) {
      throw new Error(`moveNode: mandatory targetParentId`);
    }

    let nodeBodyMove = {
      targetParentId: targetParentId,
    };
    if (newName) {
      nodeBodyMove = {
        ...nodeBodyMove,
        name: newName,
      };
    }

    if (!opts) {
      opts = {
        include: ["path"],
      };
    }

    return nodesApi.moveNode(id, nodeBodyMove, opts).catch((error) => {
      throw newPristyError("moveNode", error);
    });
  }

  createSharedLink(node) {
    const sharedLinkBody = {
      nodeId: node.id,
      expiresAt: null,
    };
    return sharedLinksApi.createSharedLink(sharedLinkBody).catch((error) => {
      throw newPristyError("createSharedLink", error);
    });
  }

  deleteSharedLink(linkId) {
    return sharedLinksApi.deleteSharedLink(linkId).catch((error) => {
      throw newPristyError("deleteSharedLink", error);
    });
  }

  revertVersion(nodeId, versionId, comment) {
    const revertBody = {
      majorVersion: false,
      comment: comment,
    };
    const opts = {
      include: ["properties"],
    };

    return versionsApi
      .revertVersion(nodeId, versionId, revertBody, opts)
      .catch((error) => {
        throw newPristyError("revertVersion", error);
      });
  }

  listNodeVersionHistory(nodeId) {
    if (this.lruVersionHistory.has(nodeId)) {
      return Promise.resolve(this.lruVersionHistory.get(nodeId));
    }

    const opts = {
      include: ["properties"],
    };

    return versionsApi
      .listVersionHistory(nodeId, opts)
      .then((data) => {
        this.lruVersionHistory.set(nodeId, data);
        return data;
      })
      .catch((error) => {
        throw newPristyError("listNodeVersionHitory", error);
      });
  }

  getVersionContent(nodeId, versionId, attachment) {
    const opts = {
      attachment: attachment,
    };
    return versionsApi
      .getVersionContent(nodeId, versionId, opts)
      .catch((error) => {
        throw newPristyError("getVersionContent", error);
      });
  }

  getSharedLink(shareId) {
    return sharedLinksApi.getSharedLink(shareId).catch((error) => {
      throw newPristyError("sharedLink", error);
    });
  }

  getSharedLinkContent(shareId) {
    const opts = {
      attachment: true,
    };
    return sharedLinksApi.getSharedLinkContent(shareId, opts).catch((error) => {
      throw newPristyError("sharedLinkContent", error.error);
    });
  }

  getSharedLinkRenditionContent(shareId) {
    return sharedLinksApi
      .getSharedLinkRenditionContent(shareId, "pdf")
      .catch((error) => {
        throw newPristyError("sharedLinkRenditionContent", error.error);
      });
  }

  getHomeFolderFromUser(idUser) {
    if (this.lruHomeFolder.has(idUser)) {
      return Promise.resolve(this.lruHomeFolder.get(idUser));
    }

    const query = {
      query: {
        language: "afts",
        query: `cm:userName:${idUser}`,
      },
      filterQueries: [{ query: 'TYPE:"cm:person"' }],
      include: ["permissions", "properties", "allowableOperations", "path"],
    };
    return searchApi.search(query).then((response) => {
      this.lruHomeFolder.set(idUser, response);
      return response;
    });
  }

  getUserHomeNode() {
    const cacheKey = "userHomeNode";
    if (this.lruHomeFolder.has(cacheKey)) {
      return Promise.resolve(this.lruHomeFolder.get(cacheKey));
    }
    // FIXME pas de pagination ici !!
    const query = {
      query: {
        language: "afts",
        query: `PATH:"//app:company_home/app:user_homes"`,
      },
      include: ["permissions", "properties", "allowableOperations", "path"],
    };
    return searchApi.search(query).then((response) => {
      this.lruHomeFolder.set(cacheKey, response);
      return response;
    });
  }

  isFolderInUserHome(nodeId, homeFolderId) {
    const cacheKey = JSON.stringify({ nodeId, homeFolderId });
    if (this.lruIsFolderInUserHome.has(cacheKey)) {
      return Promise.resolve(this.lruIsFolderInUserHome.get(cacheKey));
    }

    return this.getNode(nodeId)
      .then((node) => {
        const result = node.path.elements.some(
          (element) => element.id === homeFolderId,
        );
        this.lruIsFolderInUserHome.set(cacheKey, result);
        return result;
      })
      .catch((error) => {
        console.error("Error fetching node:", error);
        return false;
      });
  }
}

export default new AlfrescoNodeService();
